import { FC, PropsWithChildren, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import ScopedCssBaseline from '@mui/material/ScopedCssBaseline'

import logger from './logger'
import { reportErrorDetails } from './errorReporting'

const TargetDOMElement = '#page-content-wrapper #content'
const ReactAppDOMId = 'loaded-app'

/**
 * Helper component that will inject any children into the appropriate DOM element
 * within our AngularJS app. It waits for that element to exist before attempting to
 * render the React component.
 */
function AngularInjector({ children }: PropsWithChildren) {
  const [target, setTarget] = useState<Element | null>(null)
  const targetRef = useRef<Element | null>(null)

  useEffect(() => {
    const updateTarget = (newTarget: Element | null) => {
      targetRef.current = newTarget
      setTarget(newTarget)
    }

    const existingTarget =
      document.querySelector(TargetDOMElement) ||
      document.getElementById('fullscreen-content')

    if (existingTarget) {
      logger.info('AngularJS app loaded, rendering React component')
      updateTarget(existingTarget)
    } else {
      logger.info('Watching for AngularJS app to load')
    }

    // We always set up a MutationObserver, even when the target element
    // already exists. This lets us detect two scenarios:
    // 1. The target element appears later (AngularJS hasn't loaded yet)
    // 2. The target element is replaced by AngularJS after we've attached
    //    to it, which would leave our portal rendering into a detached node
    const reactTargetObserver = new MutationObserver((mutationsList) => {
      for (const mutation of mutationsList) {
        if (mutation.type === 'childList') {
          for (const node of mutation.addedNodes) {
            if (
              node instanceof HTMLElement &&
              node.nodeName === 'DIV' &&
              node.id === 'wrapper' &&
              node.querySelector('#page-content-wrapper #content')
            ) {
              // we purposefully don't disconnect our mutation observer as
              // under some conditions the AngularJS app can re-render when
              // we're on a React managed page, and without us detecting the
              // node mutation here we end up losing the React app altogether.
              // That is, the AngularJS app regenerates the DOM structure that
              // has the React app rendered into it, and in doing so removes
              // the content that we've rendered via React.
              //
              // What we have here instead is a mutation observer that is
              // always listening, and whenever it detects that the React app
              // target element has changed it will trigger a re-render of
              // the React app so that it's content is replaced.
              updateTarget(
                document.querySelector('#page-content-wrapper #content')
              )
            } else if (
              node instanceof HTMLElement &&
              node.nodeName === 'DIV' &&
              node.id === 'fullscreen-content'
            ) {
              // It's important that we don't disconnect from the observer
              // here either as this condition shares the same observer as
              // the main React app layout above.
              updateTarget(node)
            }
          }
        }
      }

      // After processing mutations, check if the DOM element our portal is
      // rendering into has been detached. AngularJS can replace elements
      // below #wrapper (e.g. #content) without replacing #wrapper itself,
      // which the addedNodes check above wouldn't catch.
      if (targetRef.current && !targetRef.current.isConnected) {
        logger.error(
          'React portal target has been detached from the DOM. Attempting to reacquire.'
        )
        const newTarget =
          document.querySelector(TargetDOMElement) ||
          document.getElementById('fullscreen-content')
        updateTarget(newTarget)
      }
    })

    const bodyNode = document.querySelector('body')

    if (bodyNode) {
      reactTargetObserver.observe(bodyNode, {
        attributes: false,
        childList: true,
        subtree: true,
      })
    } else {
      logger.error(
        'Unable to find body node to wait to inject React component into'
      )
    }
  }, [])

  if (target && !target.isConnected) {
    const message =
      'React portal target is not connected to the DOM at render time. Content will be invisible.'
    logger.error(message)
    reportErrorDetails(new Error(message))
  }

  if (target && target.querySelector(`#${ReactAppDOMId}`)) {
    const message =
      "There is already a React tree for the Loaded app rendered via a portal and we're about to inject another. This is almost certainly a bug that could cause rendering or layout issues."
    logger.warn(message)
    reportErrorDetails(new Error(message))
  }

  return target
    ? createPortal(
        <ScopedCssBaseline
          id={ReactAppDOMId}
          sx={{ backgroundColor: 'var(--page-background)' }}
        >
          {children}
        </ScopedCssBaseline>,
        target
      )
    : null
}

/**
 * A higher-order-component that will ensure the component being wrapped is
 * correctly injected into the AngularJS app in the appropriate position.
 * @param Component
 * @returns
 */
export default function withAngularJSCompatibility(Component: FC) {
  return function WithAngularJSCompabilty(props) {
    return (
      <AngularInjector>
        <Component {...props} />
      </AngularInjector>
    )
  }
}
